<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { dynamicTime } from 'shared/helpers/timeHelper';
import conversationWorkflowsAPI from 'dashboard/api/conversationWorkflows';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const router = useRouter();
const { accountId } = useAccount();
const { t } = useI18n();
const workflows = ref([]);
const isLoading = ref(true);
const isCreating = ref(false);
const deletingRowId = ref(null);

const loadWorkflows = async () => {
  isLoading.value = true;
  try {
    const { data } = await conversationWorkflowsAPI.get();
    workflows.value = data?.payload ?? [];
  } catch {
    useAlert(t('FLOW_AUTOMATIONS.INDEX.LOAD_ERROR'));
    workflows.value = [];
  } finally {
    isLoading.value = false;
  }
};

const createWorkflow = async () => {
  isCreating.value = true;
  try {
    const { data } = await conversationWorkflowsAPI.create({
      conversation_workflow: {
        name: t('FLOW_AUTOMATIONS.INDEX.DEFAULT_NAME'),
        description: '',
        active: true,
        graph: { nodes: [], edges: [] },
      },
    });
    useAlert(t('FLOW_AUTOMATIONS.INDEX.CREATE_SUCCESS'));
    const id = data?.payload?.id;
    if (id) {
      router.push({
        name: 'conversation_workflows_edit',
        params: {
          accountId: accountId.value,
          workflowId: String(id),
        },
      });
    } else {
      await loadWorkflows();
    }
  } catch {
    useAlert(t('FLOW_AUTOMATIONS.INDEX.CREATE_ERROR'));
  } finally {
    isCreating.value = false;
  }
};

const openEditor = row => {
  router.push({
    name: 'conversation_workflows_edit',
    params: {
      accountId: accountId.value,
      workflowId: String(row.id),
    },
  });
};

const deleteRow = async (row, e) => {
  e.stopPropagation();
  e.preventDefault();
  if (
    !window.confirm(
      'Excluir este fluxo? Esta ação não pode ser desfeita.'
    )
  ) {
    return;
  }
  deletingRowId.value = row.id;
  try {
    await conversationWorkflowsAPI.delete(row.id);
    useAlert('Fluxo excluído.');
    await loadWorkflows();
  } catch {
    useAlert('Não foi possível excluir o fluxo.');
  } finally {
    deletingRowId.value = null;
  }
};

const formatUpdated = ts => {
  if (!ts) return '—';
  return dynamicTime(ts);
};

onMounted(() => {
  loadWorkflows();
});
</script>

<template>
  <SettingsLayout :no-records-found="false" class="gap-6">
    <template #header>
      <div class="flex flex-col gap-4 w-full md:flex-row md:items-start md:justify-between">
        <BaseSettingsHeader
          :title="$t('FLOW_AUTOMATIONS.INDEX.HEADER.TITLE')"
          :description="$t('FLOW_AUTOMATIONS.INDEX.HEADER.DESCRIPTION')"
          feature-name="conversation-workflows"
        />
        <Button
          :label="$t('FLOW_AUTOMATIONS.INDEX.NEW')"
          icon="i-lucide-plus"
          :is-loading="isCreating"
          @click="createWorkflow"
        />
      </div>
    </template>

    <template #body>
      <div
        v-if="isLoading"
        class="flex justify-center py-12 text-n-slate-11"
      >
        {{ $t('FLOW_AUTOMATIONS.INDEX.LOADING') }}
      </div>

      <div
        v-else-if="!workflows.length"
        class="rounded-lg border border-n-weak bg-n-alpha-1 px-6 py-12 text-center text-n-slate-11"
      >
        {{ $t('FLOW_AUTOMATIONS.INDEX.EMPTY') }}
      </div>

      <div
        v-else
        class="overflow-hidden rounded-lg border border-n-weak divide-y divide-n-weak"
      >
        <div
          v-for="row in workflows"
          :key="row.id"
          class="flex flex-col gap-2 px-4 py-4 md:flex-row md:items-center md:justify-between md:gap-3 bg-n-solid-1"
        >
          <div
            role="button"
            tabindex="0"
            class="min-w-0 flex-1 flex flex-col gap-0.5 cursor-pointer rounded-md outline-none hover:bg-n-alpha-2 focus-visible:ring-2 focus-visible:ring-n-teal-9 -mx-1 px-1 py-0.5 md:py-1"
            @click="openEditor(row)"
            @keydown.enter.prevent="openEditor(row)"
            @keydown.space.prevent="openEditor(row)"
          >
            <span class="font-medium text-n-slate-12 truncate">
              {{ row.name }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{
                row.active
                  ? $t('FLOW_AUTOMATIONS.INDEX.ACTIVE')
                  : $t('FLOW_AUTOMATIONS.INDEX.INACTIVE')
              }}
              · {{ formatUpdated(row.updated_at) }}
            </span>
          </div>
          <button
            type="button"
            class="inline-flex h-9 w-9 shrink-0 items-center justify-center self-end rounded-lg text-n-slate-11 transition-colors hover:bg-n-ruby-3 hover:text-n-ruby-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-ruby-9 disabled:pointer-events-none disabled:opacity-40 md:self-center"
            :disabled="deletingRowId === row.id"
            title="Excluir fluxo"
            aria-label="Excluir fluxo"
            @click="deleteRow(row, $event)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="size-[18px] shrink-0"
              aria-hidden="true"
            >
              <path d="M3 6h18" />
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
              <line
                x1="10"
                x2="10"
                y1="11"
                y2="17"
              />
              <line
                x1="14"
                x2="14"
                y1="11"
                y2="17"
              />
            </svg>
          </button>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
